import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useAuth } from '@/hooks/useAuth';
import {
  THEMES,
  DEFAULT_THEME_PRESET,
  isThemePreset,
  resolveTheme,
  type ThemeComponents,
  type ThemeManifest,
  type ThemePreset,
} from '@/themes';

const PRESET_CACHE_KEY = 'blazepos-theme-preset';
const PRESET_LAST_KEY = 'blazepos-theme-preset-last';
const ALL_CLASSES = Object.values(THEMES).map((t) => t.className);

interface ThemePresetState {
  preset: ThemePreset;
  manifest: ThemeManifest;
}

const ThemePresetContext = createContext<ThemePresetState | undefined>(undefined);

function readCachedPreset(orgId: string | null | undefined): ThemePreset | null {
  if (typeof window === 'undefined') return null;
  if (!orgId) return null;
  try {
    const raw = window.localStorage.getItem(`${PRESET_CACHE_KEY}:${orgId}`);
    return isThemePreset(raw) ? raw : null;
  } catch {
    return null;
  }
}

function writeCachedPreset(orgId: string | null | undefined, preset: ThemePreset) {
  if (typeof window === 'undefined') return;
  try {
    if (orgId) {
      window.localStorage.setItem(`${PRESET_CACHE_KEY}:${orgId}`, preset);
    }
    window.localStorage.setItem(PRESET_LAST_KEY, preset);
  } catch {
    /* ignore quota errors */
  }
}

function applyPresetClass(preset: ThemePreset) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  const target = THEMES[preset].className;
  for (const cls of ALL_CLASSES) {
    if (cls !== target && root.classList.contains(cls)) {
      root.classList.remove(cls);
    }
  }
  if (!root.classList.contains(target)) {
    root.classList.add(target);
  }
}

export function ThemePresetProvider({ children }: { children: React.ReactNode }) {
  const { organization } = useAuth();

  const orgId = organization?.id ?? null;
  const orgPreset = (organization?.settings as { appearance?: { preset?: unknown } } | null | undefined)
    ?.appearance?.preset;

  const [preset, setPreset] = useState<ThemePreset>(() => {
    const cached = readCachedPreset(orgId);
    if (cached) return cached;
    return DEFAULT_THEME_PRESET;
  });

  const resolve = useCallback(
    (value: unknown): ThemePreset => (isThemePreset(value) ? value : DEFAULT_THEME_PRESET),
    []
  );

  useEffect(() => {
    if (orgPreset !== undefined) {
      const next = resolve(orgPreset);
      setPreset(next);
      writeCachedPreset(orgId, next);
      return;
    }
    if (!orgId) {
      setPreset(DEFAULT_THEME_PRESET);
    }
  }, [orgPreset, orgId, resolve]);

  useEffect(() => {
    applyPresetClass(preset);
  }, [preset]);

  const value = useMemo<ThemePresetState>(
    () => ({
      preset,
      manifest: resolveTheme(preset),
    }),
    [preset]
  );

  return (
    <ThemePresetContext.Provider value={value}>
      {children}
    </ThemePresetContext.Provider>
  );
}

export function useThemePreset(): ThemePresetState {
  const ctx = useContext(ThemePresetContext);
  if (!ctx) {
    return {
      preset: DEFAULT_THEME_PRESET,
      manifest: resolveTheme(DEFAULT_THEME_PRESET),
    };
  }
  return ctx;
}

export function useThemeComponents(): ThemeComponents {
  return useThemePreset().manifest.components;
}
